import { useCallback, useMemo } from 'react';
import { useCreateKeywordPin } from '../data/useCreateKeywordPin';
import { useDeleteKeywordPin } from '../data/useDeleteKeywordPin';
import { useKeywordPinsQuery } from '../data/useKeywordPinsQuery';
import { useUpdateKeywordPin } from '../data/useUpdateKeywordPin';
import { DEFAULT_KEYWORD_PINS } from './defaults';
import { mergeKeywordPins } from './merge';
import type { KeywordPinView, WritePinBody } from './serverTypes';
import type { KeywordPin, NewKeywordPin } from './types';

/**
 * React hook for the user's keyword pins, backed by the server-side
 * `user/coffee/customizations/keywordPins` API (see
 * docs/plans/2026-06-02-server-backed-keyword-pins.md).
 *
 * Hybrid model: built-ins live in code (`DEFAULT_KEYWORD_PINS`); the server only
 * stores custom pins and overrides of built-ins (a disable toggle or a content
 * edit). `mergeKeywordPins` overlays the server rows onto the defaults for reads.
 *
 * Writes go through three single-verb mutation hooks (create/update/delete),
 * each invalidating the `keywordPins` query on success — a refetch is the source
 * of truth (no optimistic cache writes). The only non-obvious bit is below: the
 * first time a built-in is edited or toggled there is no server row, so it's a
 * POST that creates an override row; subsequent changes PUT that row. Built-ins
 * are never deletable. The public API is unchanged from the previous
 * `localStorage`-backed version, so consumers (`FocusedLeadView`, the pin UI)
 * need no changes; built-ins render from the code defaults while the GET is in
 * flight and hydrate when it resolves.
 */

/** Public shape returned by `useKeywordPins`. */
export type UseKeywordPinsResult = {
	pins: KeywordPin[];
	create: (input: NewKeywordPin) => KeywordPin;
	update: (id: string, patch: Partial<KeywordPin>) => void;
	remove: (id: string) => void;
	toggleEnabled: (id: string) => void;
	matchKeyword: (text: string) => KeywordPin | null;
};

/** Did the patch touch a content field (label/keyword/color/prompt/trigger)? */
function isContentChange(patch: Partial<KeywordPin>): boolean {
	return 'label' in patch || 'keyword' in patch || 'color' in patch || 'prompt' in patch || 'trigger' in patch;
}

/** The server override row for a code built-in, matched by its locked label. */
function overrideRowFor(def: KeywordPin, rows: ReadonlyArray<KeywordPinView>): KeywordPinView | undefined {
	return rows.find(row => row.builtin && row.label === def.label);
}

/** POST body that creates the first override row for a built-in (patch over the default content). */
function builtinOverrideBody(def: KeywordPin, patch: Partial<KeywordPin>, userEdited: boolean): WritePinBody {
	return {
		label: def.label,
		keyword: patch.keyword ?? def.keyword,
		color: patch.color ?? def.color,
		prompt: patch.prompt ?? def.prompt,
		trigger: patch.trigger ?? def.trigger,
		enabled: patch.enabled ?? def.enabled,
		builtin: true,
		userEdited,
	};
}

/**
 * Apply a patch onto an existing row for a PUT. A built-in override is stamped
 * `userEdited: true` once its content changes (a plain enable/disable toggle is
 * not a content edit, so it leaves `userEdited` alone).
 */
function patchedRow(row: KeywordPinView, patch: Partial<KeywordPin>): KeywordPinView {
	const next: KeywordPinView = { ...row };
	if (patch.label !== undefined) next.label = patch.label;
	if (patch.keyword !== undefined) next.keyword = patch.keyword;
	if (patch.color !== undefined) next.color = patch.color;
	if (patch.prompt !== undefined) next.prompt = patch.prompt;
	if (patch.trigger !== undefined) next.trigger = patch.trigger;
	if (patch.enabled !== undefined) next.enabled = patch.enabled;
	if (row.builtin && isContentChange(patch)) next.userEdited = true;
	return next;
}

export function useKeywordPins(): UseKeywordPinsResult {
	const { data } = useKeywordPinsQuery();
	const { mutate: createPin } = useCreateKeywordPin();
	const { mutate: updatePin } = useUpdateKeywordPin();
	const { mutate: deletePin } = useDeleteKeywordPin();

	// Raw server rows (custom pins + built-in overrides). Empty while loading or
	// on error, in which case `merge` returns the code defaults alone.
	const rows = useMemo(() => data ?? [], [data]);

	const pins = useMemo(() => mergeKeywordPins(rows, DEFAULT_KEYWORD_PINS), [rows]);

	const create = useCallback(
		(input: NewKeywordPin): KeywordPin => {
			createPin({
				label: input.label,
				keyword: input.keyword,
				color: input.color,
				prompt: input.prompt,
				trigger: input.trigger,
				enabled: input.enabled ?? true,
				builtin: false,
			});
			// The server assigns the real row; the query refetch delivers it.
			// `handleSavePin` ignores this return — it exists only for the signature.
			return {
				id: '',
				label: input.label,
				keyword: input.keyword,
				color: input.color,
				prompt: input.prompt,
				trigger: input.trigger,
				enabled: input.enabled ?? true,
				builtin: false,
			};
		},
		[createPin]
	);

	const update = useCallback(
		(id: string, patch: Partial<KeywordPin>): void => {
			const def = DEFAULT_KEYWORD_PINS.find(d => d.id === id);
			if (def) {
				// Built-in: PUT its existing override row, or POST the first one.
				const row = overrideRowFor(def, rows);
				if (row) {
					updatePin({ id: row.id, row: patchedRow(row, patch) });
				} else {
					createPin(builtinOverrideBody(def, patch, isContentChange(patch)));
				}
				return;
			}
			// Custom pin: PUT its row (no-op if the GET hasn't delivered it yet).
			const row = rows.find(r => r.id === id);
			if (row) {
				updatePin({ id: row.id, row: patchedRow(row, patch) });
			}
		},
		[rows, createPin, updatePin]
	);

	const remove = useCallback(
		(id: string): void => {
			// Built-ins are not deletable — disable via the toggle instead.
			if (DEFAULT_KEYWORD_PINS.some(d => d.id === id)) {
				return;
			}
			const row = rows.find(r => r.id === id);
			if (row) {
				deletePin(row.id);
			}
		},
		[rows, deletePin]
	);

	const toggleEnabled = useCallback(
		(id: string): void => {
			// Flip against the currently rendered state (default- or row-derived);
			// `update` routes the write to the right POST/PUT.
			const pin = pins.find(p => p.id === id);
			if (pin) {
				update(id, { enabled: !pin.enabled });
			}
		},
		[pins, update]
	);

	const matchKeyword = useCallback(
		(text: string): KeywordPin | null => {
			const normalized = text.toLowerCase().trim();
			if (!normalized) {
				return null;
			}
			for (const pin of pins) {
				if (!pin.enabled) {
					continue;
				}
				const keyword = pin.keyword.toLowerCase();
				if (!keyword) {
					continue;
				}
				if (normalized.startsWith(keyword)) {
					return pin;
				}
			}
			return null;
		},
		[pins]
	);

	return useMemo(
		() => ({ pins, create, update, remove, toggleEnabled, matchKeyword }),
		[pins, create, update, remove, toggleEnabled, matchKeyword]
	);
}
